import { React, useEffect, useState, useRef, useMemo } from "react";
import { genericGet, genericPost } from "../../services/Api";
import cogoToast from "cogo-toast";
import { Spinner } from "reactstrap";
import { useHistory, Link } from "react-router-dom";
import { Breadcrumb, BreadcrumbItem } from "reactstrap";
import { SUCCESS_RESULT, ENDPOINTS } from "../../Constants";
import { EbmsLoading, log } from "../../services/Utils";
import Select from "react-select";
import "./style.css";
import AppContext from "../../AppContext";

// Native Tree Node Component
const TreeNode = ({ node, checked, originalChecked, expanded, onCheck, onExpand, level = 0 }) => {
  const hasChildren = node.children && node.children.length > 0;
  const isLeaf = !hasChildren;
  const isExpanded = expanded.includes(node.value);
  const isChecked = checked.includes(node.value);
  const wasOriginallyChecked = originalChecked.includes(node.value);
  const isBeingRemoved = wasOriginallyChecked && !isChecked; // Unchecked but was originally checked
  
  const handleToggle = () => {
    if (hasChildren) {
      onExpand(node.value);
    }
  };

  const handleCheck = (e) => {
    e.stopPropagation();
    // Allow checking both leaf nodes and parent nodes
    onCheck(node.value);
  };

  // Determine background color based on state
  let backgroundColor = 'transparent';
  let borderColor = 'transparent';
  let boxShadow = 'none';
  
  if (isBeingRemoved) {
    backgroundColor = '#ffebee';
    borderColor = '#f44336';
    boxShadow = '0 2px 4px rgba(244, 67, 54, 0.2)';
  } else if (isChecked) {
    backgroundColor = '#e3f2fd';
    borderColor = '#2196f3';
    boxShadow = '0 2px 4px rgba(33, 150, 243, 0.2)';
  }

  return (
    <div className="tree-node">
      <div 
        className={`tree-node-content ${isExpanded ? 'expanded' : ''} ${isChecked ? 'checked' : ''} ${isBeingRemoved ? 'removed' : ''}`}
        style={{ 
          display: 'flex', 
          alignItems: 'center', 
          padding: '8px 12px',
          marginLeft: `${level * 20}px`,
          cursor: hasChildren ? 'pointer' : 'default',
          borderRadius: '6px',
          transition: 'all 0.2s ease',
          marginBottom: '3px',
          backgroundColor: backgroundColor,
          border: `1px solid ${borderColor}`,
          boxShadow: boxShadow
        }}
        onClick={handleToggle}
        onMouseEnter={(e) => {
          if (!isChecked && !isBeingRemoved) {
            e.currentTarget.style.backgroundColor = '#f5f5f5';
            e.currentTarget.style.borderColor = '#e0e0e0';
          }
        }}
        onMouseLeave={(e) => {
          if (!isChecked && !isBeingRemoved) {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.borderColor = 'transparent';
          }
        }}
      >
        {/* Expand/Collapse Icon */}
        {hasChildren && (
          <span 
            style={{ 
              marginRight: '10px', 
              fontSize: '12px',
              width: '18px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: isExpanded ? '#2196f3' : '#42a5f5',
              transition: 'all 0.2s ease',
              fontWeight: 'bold'
            }}
          >
            {isExpanded ? '▼' : '▶'}
          </span>
        )}
        {!hasChildren && (
          <span style={{ 
            marginRight: '24px', 
            fontSize: '10px', 
            color: '#66bb6a',
            width: '18px',
            display: 'inline-block',
            textAlign: 'center',
            fontWeight: 'bold'
          }}>
            ●
          </span>
        )}
        
        {/* Checkbox */}
        <input
          type="checkbox"
          checked={isChecked}
          onChange={handleCheck}
          onClick={(e) => e.stopPropagation()}
          style={{
            marginRight: '12px',
            cursor: 'pointer',
            width: '20px',
            height: '20px',
            accentColor: isBeingRemoved ? '#f44336' : '#2196f3',
            opacity: 1,
            transform: 'scale(1.1)',
            border: isBeingRemoved ? '2px solid #f44336' : 'none',
            borderRadius: isBeingRemoved ? '2px' : 'none'
          }}
        />
        
        {/* Label */}
        <span 
          style={{ 
            flex: 1,
            fontSize: '14px',
            color: isBeingRemoved ? '#d32f2f' : (isChecked ? '#1976d2' : (isLeaf ? '#212121' : '#424242')),
            fontWeight: isChecked || isBeingRemoved ? '600' : (isLeaf ? '500' : '600'),
            lineHeight: '1.6',
            textDecoration: isBeingRemoved ? 'line-through' : 'none',
            opacity: isBeingRemoved ? 0.8 : 1
          }}
        >
          {node.label}
        </span>
      </div>
      
      {/* Children */}
      {hasChildren && isExpanded && (
        <div className="tree-node-children" style={{ marginTop: '2px' }}>
          {node.children.map((child) => (
            <TreeNode
              key={child.value}
              node={child}
              checked={checked}
              originalChecked={originalChecked}
              expanded={expanded}
              onCheck={onCheck}
              onExpand={onExpand}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Card Component for each main group
const GroupCard = ({ node, checked, originalChecked, expanded, onCheck, onExpand }) => {
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expanded.includes(node.value);
  const isMainModuleChecked = checked.includes(node.value);
  const wasMainModuleOriginallyChecked = originalChecked.includes(node.value);
  const isMainModuleBeingRemoved = wasMainModuleOriginallyChecked && !isMainModuleChecked;
  
  // Count leaf nodes (checkable permissions) in this card's subtree
  const countLeafNodes = (node) => {
    if (!node.children || node.children.length === 0) {
      return 1; // This is a leaf node
    }
    let count = 0;
    node.children.forEach(child => {
      count += countLeafNodes(child);
    });
    return count;
  };

  // Count checked leaf nodes in this card's subtree
  const countCheckedLeafNodes = (node, checked) => {
    if (!node.children || node.children.length === 0) {
      return checked.includes(node.value) ? 1 : 0; // Leaf node, check if it's checked
    }
    let count = 0;
    node.children.forEach(child => {
      count += countCheckedLeafNodes(child, checked);
    });
    return count;
  };

  const totalInCard = countLeafNodes(node);
  const checkedInCard = countCheckedLeafNodes(node, checked);
  
  const handleCardExpand = () => {
    if (hasChildren) {
      onExpand(node.value);
    }
  };

  const handleMainModuleCheck = (e) => {
    e.stopPropagation();
    onCheck(node.value);
  };

  // Determine card background and border based on main module check state
  let cardBackgroundColor = '#ffffff';
  let cardBorderColor = isMainModuleChecked ? '#2196f3' : (isMainModuleBeingRemoved ? '#f44336' : '#2196f3');
  let cardBorderWidth = isMainModuleChecked ? '3px' : (isMainModuleBeingRemoved ? '3px' : '2px');
  let cardBoxShadow = isMainModuleChecked 
    ? '0 4px 16px rgba(33, 150, 243, 0.3)' 
    : (isMainModuleBeingRemoved ? '0 4px 16px rgba(244, 67, 54, 0.3)' : '0 4px 12px rgba(33, 150, 243, 0.15)');

  if (isMainModuleChecked) {
    cardBackgroundColor = '#e3f2fd';
  } else if (isMainModuleBeingRemoved) {
    cardBackgroundColor = '#ffebee';
  }

  return (
    <div className="group-card" style={{
      minWidth: '320px',
      maxWidth: '380px',
      flex: '1 1 320px',
      backgroundColor: cardBackgroundColor,
      border: `${cardBorderWidth} solid ${cardBorderColor}`,
      borderRadius: '12px',
      padding: '20px',
      boxShadow: cardBoxShadow,
      transition: 'all 0.3s ease',
      display: 'flex',
      flexDirection: 'column',
      maxHeight: '650px',
      overflow: 'hidden'
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.boxShadow = '0 6px 20px rgba(33, 150, 243, 0.25)';
      e.currentTarget.style.transform = 'translateY(-2px)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.boxShadow = cardBoxShadow;
      e.currentTarget.style.transform = 'translateY(0)';
    }}
    >
      {/* Card Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '16px',
        paddingBottom: '12px',
        borderBottom: '2px solid #e3f2fd'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          cursor: hasChildren ? 'pointer' : 'default',
          flex: 1
        }}
        onClick={handleCardExpand}
        >
          {/* Main Module Checkbox */}
          <input
            type="checkbox"
            checked={isMainModuleChecked}
            onChange={handleMainModuleCheck}
            onClick={(e) => e.stopPropagation()}
            style={{
              cursor: 'pointer',
              width: '22px',
              height: '22px',
              accentColor: isMainModuleBeingRemoved ? '#f44336' : '#2196f3',
              transform: 'scale(1.15)',
              border: isMainModuleBeingRemoved ? '2px solid #f44336' : 'none',
              borderRadius: isMainModuleBeingRemoved ? '2px' : 'none',
              marginRight: '8px'
            }}
            title={isMainModuleChecked ? 'Uncheck main module' : 'Check main module'}
          />
          
          {hasChildren && (
            <span style={{
              fontSize: '14px',
              color: isExpanded ? '#2196f3' : '#42a5f5',
              fontWeight: 'bold',
              transition: 'transform 0.2s ease'
            }}>
              {isExpanded ? '▼' : '▶'}
            </span>
          )}
          <h3 style={{
            margin: 0,
            fontSize: '16px',
            fontWeight: '700',
            color: isMainModuleBeingRemoved ? '#d32f2f' : (isMainModuleChecked ? '#1976d2' : '#1976d2'),
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            textDecoration: isMainModuleBeingRemoved ? 'line-through' : 'none',
            opacity: isMainModuleBeingRemoved ? 0.8 : 1
          }}>
            <span>{node.label}</span>
            <span style={{
              fontSize: '13px',
              fontWeight: '600',
              color: checkedInCard === totalInCard ? '#4caf50' : (checkedInCard > 0 ? '#ff9800' : '#999'),
              backgroundColor: checkedInCard === totalInCard ? '#e8f5e9' : (checkedInCard > 0 ? '#fff3e0' : '#f5f5f5'),
              padding: '2px 8px',
              borderRadius: '12px',
              border: `1px solid ${checkedInCard === totalInCard ? '#4caf50' : (checkedInCard > 0 ? '#ff9800' : '#ddd')}`
            }}>
              ({checkedInCard}/{totalInCard})
            </span>
          </h3>
        </div>
      </div>

      {/* Card Content - Tree Nodes */}
      {hasChildren && isExpanded && (
        <div style={{
          overflowY: 'auto',
          flex: 1,
          paddingRight: '8px'
        }}
        className="card-tree-content"
        >
          {node.children.map((child) => (
            <TreeNode
              key={child.value}
              node={child}
              checked={checked}
              originalChecked={originalChecked}
              expanded={expanded}
              onCheck={onCheck}
              onExpand={onExpand}
              level={0}
            />
          ))}
        </div>
      )}
      
      {!hasChildren && (
        <div style={{
          padding: '20px',
          textAlign: 'center',
          color: '#999',
          fontStyle: 'italic'
        }}>
          No sub-items
        </div>
      )}
    </div>
  );
};

// Native Tree Component - Now displays cards horizontally
const NativeTree = ({ nodes, checked, originalChecked, expanded, onCheck, onExpand }) => {
  // Function to count all leaf nodes (checkable permissions)
  const countLeafNodes = (nodes) => {
    let count = 0;
    nodes.forEach(node => {
      if (node.children && node.children.length > 0) {
        count += countLeafNodes(node.children);
      } else {
        count += 1; // This is a leaf node
      }
    });
    return count;
  };

  const totalPermissions = countLeafNodes(nodes);
  const assignedPermissions = checked.length;
  const progressPercentage = totalPermissions > 0 ? (assignedPermissions / totalPermissions) * 100 : 0;

  const handleExpandAll = () => {
    const getAllValues = (nodes) => {
      let values = [];
      nodes.forEach(node => {
        if (node.children && node.children.length > 0) {
          values.push(node.value);
          values = values.concat(getAllValues(node.children));
        }
      });
      return values;
    };
    onExpand(getAllValues(nodes));
  };

  const handleCollapseAll = () => {
    onExpand([]);
  };

  return (
    <div style={{ 
      width: '100%'
    }}>
      {/* Progress Bar */}
      <div style={{
        marginBottom: '12px',
        padding: '8px 12px',
        backgroundColor: '#f5f5f5',
        borderRadius: '6px',
        border: '1px solid #e0e0e0'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '6px'
        }}>
          <span style={{
            fontSize: '12px',
            color: '#666',
            fontWeight: '500'
          }}>
            Permissions Progress
          </span>
          <span style={{
            fontSize: '12px',
            color: '#2196f3',
            fontWeight: '600'
          }}>
            {assignedPermissions} / {totalPermissions}
          </span>
        </div>
        <div style={{
          width: '100%',
          height: '8px',
          backgroundColor: '#e0e0e0',
          borderRadius: '4px',
          overflow: 'hidden',
          position: 'relative'
        }}>
          <div style={{
            width: `${progressPercentage}%`,
            height: '100%',
            backgroundColor: progressPercentage === 100 ? '#4caf50' : '#2196f3',
            borderRadius: '4px',
            transition: 'width 0.3s ease, background-color 0.3s ease',
            boxShadow: progressPercentage > 0 ? '0 1px 3px rgba(33, 150, 243, 0.3)' : 'none'
          }}></div>
        </div>
      </div>

      {/* Control Buttons */}
      <div style={{ 
        marginBottom: '12px', 
        display: 'flex', 
        gap: '8px',
        paddingBottom: '8px',
        borderBottom: '1px solid #e3f2fd',
        justifyContent: 'flex-start',
        alignItems: 'center'
      }}>
        <button
          onClick={handleExpandAll}
          style={{
            padding: '6px 14px',
            fontSize: '12px',
            backgroundColor: '#2196f3',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            fontWeight: '500',
            boxShadow: '0 2px 4px rgba(33, 150, 243, 0.2)',
            textTransform: 'none',
            letterSpacing: '0'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#1976d2';
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 3px 6px rgba(33, 150, 243, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#2196f3';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 4px rgba(33, 150, 243, 0.2)';
          }}
        >
          Expand All
        </button>
        <button
          onClick={handleCollapseAll}
          style={{
            padding: '6px 14px',
            fontSize: '12px',
            backgroundColor: '#ff9800',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            fontWeight: '500',
            boxShadow: '0 2px 4px rgba(255, 152, 0, 0.2)',
            textTransform: 'none',
            letterSpacing: '0'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f57c00';
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 3px 6px rgba(255, 152, 0, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#ff9800';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 4px rgba(255, 152, 0, 0.2)';
          }}
        >
          Collapse All
        </button>
      </div>
      
      {/* Cards Container - Horizontal Layout */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '20px',
        justifyContent: 'flex-start',
        alignItems: 'flex-start'
      }}>
        {nodes.map((node) => (
          <GroupCard
            key={node.value}
            node={node}
            checked={checked}
            originalChecked={originalChecked}
            expanded={expanded}
            onCheck={onCheck}
            onExpand={onExpand}
          />
        ))}
      </div>
    </div>
  );
};

const AccessRights = (props) => {
  const history = useHistory();

  const [isLoading, setIsLoading] = useState(false);
  const [allRights, setAllRights] = useState([]);
  // const [assignedRights, setAssignedRights] = useState([]);
  const [users, setUsers] = useState([]);
  const [checked, setChecked] = useState([]);
  const [originalChecked, setOriginalChecked] = useState([]); // Track originally assigned permissions
  const [expanded, setExpanded] = useState([]);
  const [user, setUser] = useState("");
  const [selectedUser, setSelectedUser] = useState(null); // For react-select
  const [detail, setDetail] = useState({ id: "" });


  const handleChange = (selectedOption) => {
    // log("Handle Change: ","Changed the loading data");
    if (selectedOption) {
      setDetail({ ...detail, id: selectedOption.value });
      setUser(selectedOption.value);
      setSelectedUser(selectedOption);
      // Use selectedOption.value instead of user since user state hasn't updated yet
      loadAssignedRights(selectedOption.value);

      // log("Selected:", selectedOption.value)
    } else {
      // User cleared the selection
      setDetail({ ...detail, id: "" });
      setUser("");
      setSelectedUser(null);
    }
  };

  const handleRefresh = () => {
    if (user && user !== "") {
      loadAssignedRights(user);
      cogoToast.info("Refreshing permissions...", {});
    } else {
      cogoToast.warn("Please select a user first", {});
    }
  };



  const onCheck = (value) => {
    // Toggle the checked state for the clicked node
    if (checked.includes(value)) {
      setChecked(checked.filter(item => item !== value));
    } else {
      setChecked([...checked, value]);
    }
  };

  const onExpand = (valueOrArray) => {
    // Handle both single value toggle and array (for expand all/collapse all)
    if (Array.isArray(valueOrArray)) {
      setExpanded(valueOrArray);
    } else {
      // Toggle the expanded state for the clicked node
      if (expanded.includes(valueOrArray)) {
        setExpanded(expanded.filter(item => item !== valueOrArray));
      } else {
        setExpanded([...expanded, valueOrArray]);
      }
    }
  };
  useEffect(() => {
    loadAllRights();
    // loadAssignedRights();
    loadusers();

    return () => { };
  }, []);

  // Auto-expand all groups when user is selected and allRights are loaded
  useEffect(() => {
    if (user && allRights.length > 0) {
      const getAllExpandableValues = (nodes) => {
        if (!nodes || nodes.length === 0) return [];
        let values = [];
        nodes.forEach(node => {
          if (node.children && node.children.length > 0) {
            values.push(node.value);
            // Recursively get all child values
            const getChildValues = (children) => {
              let childValues = [];
              children.forEach(child => {
                if (child.children && child.children.length > 0) {
                  childValues.push(child.value);
                  childValues = childValues.concat(getChildValues(child.children));
                }
              });
              return childValues;
            };
            values = values.concat(getChildValues(node.children));
          }
        });
        return values;
      };
      
      const allExpandableValues = getAllExpandableValues(allRights);
      // Expand all groups when user is first selected
      if (allExpandableValues.length > 0) {
        setExpanded(allExpandableValues);
      }
    }
  }, [user, allRights]);

  const loadAllRights = () => {
    setIsLoading(true);
    genericGet(ENDPOINTS.ALL_RIGHTS, {})
      .then((res) => {
        setIsLoading(false);
        if (res.status == 200) {
        
          if (res.data.status == SUCCESS_RESULT) {
            let responseData = res.data.data;
            log("RIGHTS",responseData);
           log("RIGHTS: ", responseData);
            setAllRights(responseData)
          } else {
            cogoToast.error(res.data.message, {});
          }
        }
      })
      .catch((err) => {
        setIsLoading(false);
        console.log(err);
      });
  };
  const loadAssignedRights = (id) => {
    // log("GET FOR ID: ", id);
    setChecked([]);
    setOriginalChecked([]); // Reset original checked
    setExpanded([]);
    setIsLoading(true);
    genericPost(ENDPOINTS.ASSIGNED_RIGHTS, { "id": id })
      .then((res) => {
        setIsLoading(false);
        if (res.status == 200) {
          if (res.data.status == SUCCESS_RESULT) {
            let responseData = res.data.data;
            let allAssignedRights = [];
            let a=0;
            // log("COUNT>>: ", responseData.length);
            for (let q = 0; q < responseData.length; q++) {
              let perm = allAssignedRights.includes(responseData[q]["id"]);
              // log("PERM>>: ", responseData[q]["id"]);
              if (perm !== true) {
                // log("PERM: ", responseData[q]["id"]);
                allAssignedRights[a] = responseData[q]["id"];
                a++;
              }

            }
            // log("ASSIGNED RIGHTS: ", allAssignedRights);
            setChecked(allAssignedRights);
            setOriginalChecked([...allAssignedRights]); // Store original for comparison
          } else {
            cogoToast.error(res.data.message, {});
          }
        }
      })
      .catch((err) => {
        setIsLoading(false);
        console.log(err);
      });
  };
  const loadusers = () => {
    setIsLoading(true);
    genericGet(ENDPOINTS.ALL_RIGHTS_USERS, {})
      .then((res) => {
        setIsLoading(false);
        if (res.status == 200) {
          if (res.data.status == SUCCESS_RESULT) {
            let responseData = res.data.data;
            setUsers(responseData);
            // If user was previously selected, restore the selection
            if (user) {
              const userOption = responseData.find(u => u.id === user || u.value === user);
              if (userOption) {
                const option = {
                  value: userOption.id || userOption.value,
                  label: userOption.name || userOption.username || userOption.email || userOption.text || `User ${userOption.id || userOption.value}`
                };
                setSelectedUser(option);
              }
            }
          } else {
            cogoToast.error(res.data.message, {});
          }
        }
      })
      .catch((err) => {
        setIsLoading(false);
        console.log(err);
      });
  };
  const updateAssignedPermissions = () => {
    // log("TO UPDATE: ",checked);
    if (user === null || user === "") {
      cogoToast.error("Please select the user account", {});

      return;
    }
    
    // Prepare the permissions
    let dataToServer = [];
    let index = 0;
    
    // Add checked permissions with status 10001 (active)
    for (let a = 0; a < checked.length; a++) {
      dataToServer[index] = {
        "user_id": user,
        "module_permission_id": checked[a],
        "status": 10001
      };
      index++;
    }
    
    // Add unchecked permissions that were originally checked with status 10002 (inactive/removed)
    const uncheckedPermissions = originalChecked.filter(perm => !checked.includes(perm));
    for (let b = 0; b < uncheckedPermissions.length; b++) {
      dataToServer[index] = {
        "user_id": user,
        "module_permission_id": uncheckedPermissions[b],
        "status": 10002
      };
      index++;
    }
    
    // log("dataToServer", dataToServer);
    //proceed to update server
    if (dataToServer.length > 0) {
      setIsLoading(true);

      genericPost(ENDPOINTS.ASSIGN_RIGHTS_USERS, dataToServer)
        .then((res) => {
          setIsLoading(false);
          if (res.status == 200) {
            if (res.data.status == SUCCESS_RESULT) {
              cogoToast.success(res.data.message, {});
              // Update originalChecked to reflect the new state after save
              setOriginalChecked([...checked]);
            } else {
              cogoToast.error(res.data.message, {});
            }
          }
        })
        .catch((err) => {
          setIsLoading(false);
          console.log(err);
        });
    } else {
      cogoToast.warn("No permission changes to save", {});
    }
  }

  return (
    <div>
      <div class="card">
        <div class="card-header">
          <h3 class="card-title">
            <Breadcrumb className="ms-1">
              <BreadcrumbItem>
                <Link to="/ebms"> EBMS </Link>
              </BreadcrumbItem>
              <BreadcrumbItem active>
                <Link to="/access-rights"> ACCESS RIGHTS </Link>
              </BreadcrumbItem>
            </Breadcrumb>
          </h3>

        </div>
        <div className="card-body">
          <div className="d-flex justify-content-center my-1">
            <center><EbmsLoading data={isLoading} /></center>
          </div>
          <div class="col-md-12 row">
           
            <div class="col-md-5">
            <Select
                    value={selectedUser}
                    onChange={handleChange}
                    options={users?.map((u) => ({
                      value: u.id || u.value,
                      label: u.name || u.username || u.email || u.text || `User ${u.id || u.value}`
                    }))}
                    placeholder="Select a user..."
                    isSearchable={true}
                    isClearable={true}
                    styles={{
                      control: (base) => ({
                        ...base,
                        flex: 1,
                        minHeight: '38px'
                      }),
                      container: (base) => ({
                        ...base,
                        flex: 1
                      })
                    }}
                  />
            </div>
            <div class="col-md-2">
            <button
                    type="button"
                    onClick={handleRefresh}
                    className="btn btn-sm btn-secondary"
                    style={{
                      padding: '6px 12px',
                      fontSize: '12px',
                      minWidth: '80px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px'
                    }}
                    title="Refresh permissions for selected user"
                  >
                    <i className="fa fa-refresh"></i> Refresh
                  </button>
            </div>
            <div class="col-md-3 pull-right">
            <button type="button" onClick={updateAssignedPermissions} class=" btn btn-info btn-md">Save Permission Changes</button>

            </div>
          </div>
          <div class="col-md-12 row">
            <div class="dropdown-divider"></div>
          </div>
          <div class="col-md-12 row">

 
            <div style={{ marginTop: '10px' }}>
              <NativeTree
                nodes={allRights}
                checked={checked}
                originalChecked={originalChecked}
                expanded={expanded}
                onCheck={onCheck}
                onExpand={onExpand}
              />
            </div>

            
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccessRights;
